import React, { useEffect, useRef } from "react";

import { getStatusTexts, STATUS } from "./status-texts";
import "./chrome.css";

export { STATUS };

/* heroicons/outline, inline wie DownloadIcon in ReportHeader.js. */
const ClockIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5V12l3 1.5" />
  </svg>
);

const ExclamationIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v4.5" />
    <path d="M12 15.75h.01" />
  </svg>
);

const ICONS = {
  [STATUS.LINK_EXPIRED]: ClockIcon,
  [STATUS.APP_FAILED]: ExclamationIcon,
};

/**
 * Die Seite, wenn es keinen Report zu zeigen gibt: Link abgelaufen oder die
 * App ist beim Laden gescheitert. Auch der Fallback der Bugsnag-ErrorBoundary
 * in src/index.js — deshalb ohne Abhaengigkeiten, die selbst scheitern
 * koennten, und mit app_failed als Auffangnetz fuer unbekannte Gruende.
 *
 * Keine Kopfzeile: die existiert in PdfShell fuer den Download, und hier gibt
 * es nichts herunterzuladen. Eine leere Leiste waere ein Versprechen ohne
 * Inhalt. Marke tragen Tab-Titel, Badge und der Text ("Aivy-Dashboard").
 *
 * Genau eine Hauptaktion: Der abgelaufene Link laesst sich von hier aus nicht
 * erneuern (die Seite hat keine Anmeldung und keine Kennungen), also fuehrt
 * der Weg zurueck dorthin, wo der Report angefordert wurde. Zurueck- oder
 * Schliessen-Knoepfe gibt es bewusst nicht: das Dashboard oeffnet den Report
 * in einem neuen Tab (window.open + location.href), dessen History nur
 * about:blank kennt; ein wiederhergestellter Safari-Tab hat gar keine.
 *
 * Setzt NICHT window.pdfurl. pdfGenerator wartet darauf und laeuft in seinen
 * Timeout — richtig so, denn es gibt kein PDF (s. PdfShell.test.js).
 *
 * @param {Object} props
 * @param {string} props.reason - STATUS-Wert
 * @param {string} [props.language] - "de" | "en"
 */
const StatusScreen = ({ reason, language }) => {
  const texts = getStatusTexts(reason, language);
  const Icon = ICONS[texts.code];
  const headingRef = useRef(null);

  useEffect(() => {
    /* Tab-Titel und Dokumentsprache passen zur Meldung — der Tab hiesse sonst
       weiter "Ergebnisprofil", obwohl keines da ist, und Screenreader laesen
       englische Saetze mit deutscher Stimme. */
    document.title = texts.documentTitle;
    /* texts.language, nicht die rohe Prop: bei einer unbekannten Sprache steht
       deutscher Text auf der Karte, und lang muss das auch sagen. */
    document.documentElement.lang = texts.language;

    /* Fokus auf die Ueberschrift, nicht auf den Button: Die Seite wechselt
       ohne Navigation vom Spinner zur Meldung, ein Screenreader bekaeme das
       sonst nicht mit. Von der Ueberschrift aus liest er Titel und Grund und
       erreicht mit Tab die einzige Aktion. Keine Live-Region: die ganze Seite
       ist die Meldung, ein role="alert" wuerde sie doppelt ansagen. */
    headingRef.current?.focus({ preventScroll: true });
  }, [texts.documentTitle, texts.language]);

  return (
    <main
      className="aivy-card-area aivy-status-area"
      aria-labelledby="aivy-status-title"
    >
      <div className="aivy-card aivy-status" data-status={texts.code}>
        <div className="aivy-status__icon" aria-hidden="true">
          <Icon />
        </div>
        <h1
          id="aivy-status-title"
          className="aivy-card__title"
          ref={headingRef}
          tabIndex={-1}
        >
          {texts.title}
        </h1>
        <p id="aivy-status-text" className="aivy-card__text">
          {texts.body}
        </p>
        <div className="aivy-card__actions">
          {texts.primary.href ? (
            <a
              className="aivy-button"
              href={texts.primary.href}
              aria-describedby="aivy-status-text"
            >
              {texts.primary.label}
            </a>
          ) : (
            <button
              className="aivy-button"
              type="button"
              onClick={() => window.location.reload()}
              aria-describedby="aivy-status-text"
            >
              {texts.primary.label}
            </button>
          )}
          <a className="aivy-link" href={texts.secondary.href}>
            {texts.secondary.label}
          </a>
        </div>
        <p className="aivy-card__meta">
          {texts.codeLabel}: <code>{texts.code}</code>
        </p>
      </div>
    </main>
  );
};

export default StatusScreen;
